import type { GameId } from "./games";

/**
 * A PUBLIC GAME OF YOURS IS STILL GOING — the one sentence both «you are already in a public game»
 * refusals say (card-a5 item 1, 2026-10-11).
 *
 * Two doors refuse a public quick-match with it, and they answer two different questions:
 *  · the game-server's in-process `live-tables` check — «you are SEATED at a live table of this same
 *    game right now» (connected, or inside its grace), since 2026-08-27;
 *  · the cross-instance SEAT HOLD (`apps/game-server/src/rooms/seat-hold.ts`) — «a public table you
 *    left mid-game without «خروج» is still holding your seat for you», the operator's rule of
 *    2026-10-11: no other public game can be joined while a seat is held.
 * Both mean the same thing to the player — a public game of theirs is not over — so they say the
 * same thing, and the client recognises it ({@link isPublicGameInProgressMessage}) to offer the way
 * back to that table instead of the generic error screen.
 *
 * ⚠️ THE WORDING IS THE ONE PROD HAS SHOWN SINCE 2026-08-27, BYTE FOR BYTE. A bundle cached before
 * this item renders a join refusal verbatim, so keeping the sentence keeps every old client's screen
 * exactly as it was — and the matcher below needs no legacy branch for a deploy window.
 *
 * ⚠️ IT NAMES NO GAME AND NO ONE ELSE: a refusal says what is true for the reader and stops (CLAUDE.md
 * «NOTHING MAY SAY WHAT A DEPARTURE OR A DISCONNECT COST THE _OTHER_ PLAYER» — pinned with the rest of
 * that copy by `apps/web/test/away-copy-rule.test.ts`).
 */
export const PUBLIC_GAME_IN_PROGRESS_MESSAGE =
  "تو همین حالا در یک بازی عمومی هستی. اول همون رو تموم کن یا ازش خارج شو.";

/** Whether a join error is THIS refusal (the client then offers the way back to the table). */
export function isPublicGameInProgressMessage(message: unknown): boolean {
  return typeof message === "string" && message.trim().startsWith(PUBLIC_GAME_IN_PROGRESS_MESSAGE);
}

/**
 * WHERE IS MY HELD SEAT? — the game-server's own answer, by initData (card-9f, the operator 2026-10-28:
 * «the refusal's two options really work»).
 *
 * The refusal above offered «بازگشت به بازی» only when the device kept the seat's ticket — and 404 of 606
 * held seats in a week were never returned to, the phone holding no key (it dropped before the dealt
 * board was on its screen, or it is another device). So a NEW bundle's refusal screen asks the
 * game-server, which reads its OWN records (the hold above; the live seat map) for the validated Telegram
 * id and names the table: `POST <game-server>/matchmake/heldseat/<any>`, body `{ initData }`, answer
 * {@link HeldSeatAnswer}. It reserves nothing and opens nothing; the seat itself is handed back by the
 * `takeover` door (`SEAT_TAKEOVER_METHOD`, origin `held`). An older game-server answers «invalid method»
 * (4210), and the screen stays exactly as it was. A string matched across the wire: a contract between
 * the game-server image and the web image (CLAUDE.md).
 */
export const HELD_SEAT_METHOD = "heldseat";

/** The held public seat the game-server named — a table in progress, or a public waiting room. */
export interface HeldSeat {
  roomId: string;
  game: GameId;
  /** True for a public WAITING ROOM (nothing dealt yet): «بازگشت به میز», and no «خروج». */
  waiting: boolean;
}

/** What {@link HELD_SEAT_METHOD} answers: the held seat, or null (nothing held, or not provable). */
export interface HeldSeatAnswer {
  held: HeldSeat | null;
}
